import { Alert, Typography } from "@mui/material";
import Snackbar from "@mui/material/Snackbar";
import React, {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
} from "react";

interface ErrorContextType {
  showError: (error: string | string[], autoHideDuration?: number) => void;
  showSuccess: (message: string | string[], autoHideDuration?: number) => void;
}

const ErrorContext = createContext<ErrorContextType>({
  showError: () => {},
  showSuccess: () => {},
});

// ErrorProvider component that manages the error state and displays a snackbar
export const ErrorProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const [error, setError] = useState<string | string[]>([]);
  const [successMessage, setSuccessMessage] = useState<string | string[]>([]);
  const [open, setOpen] = useState(false);
  const duration = useRef(3000);

  const showError = useCallback(
    (err: string | string[], autoHideDuration = 3000) => {
      if (autoHideDuration) {
        duration.current = autoHideDuration;
      }
      setError(err);
      setOpen(true);
    },
    []
  );

  const showSuccess = useCallback(
    (message: string | string[], autoHideDuration = 3000) => {
      if (autoHideDuration) {
        duration.current = autoHideDuration;
      }
      setSuccessMessage(message);
      setOpen(true);
    },
    []
  );

  const handleClose = () => {
    setOpen(false);
    setError([]);
    setSuccessMessage([]);
  };

  const errors = useMemo(() => {
    if (Array.isArray(error)) {
      return error;
    }
    return [error];
  }, [error]);

  const successMessages = useMemo(() => {
    if (Array.isArray(successMessage)) {
      return successMessage;
    }
    return [successMessage];
  }, [successMessage]);

  return (
    <ErrorContext.Provider value={{ showError, showSuccess }}>
      {children}
      {errors.length && (
        <Snackbar
          open={true}
          autoHideDuration={duration.current}
          onClose={handleClose}
          anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        >
          <Alert onClose={handleClose} severity="error" sx={{ width: "100%" }}>
            {errors.map((e, index) => (
              <Typography key={index}>{e}</Typography>
            ))}
          </Alert>
        </Snackbar>
      )}
      {successMessages.length && (
        <Snackbar
          open={true}
          autoHideDuration={duration.current}
          onClose={handleClose}
          anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        >
          <Alert onClose={handleClose} severity="success" sx={{ width: "100%" }}>
            {successMessages.map((e, index) => (
              <Typography key={index}>{e}</Typography>
            ))}
          </Alert>
        </Snackbar>
      )}
    </ErrorContext.Provider>
  );
};

// Custom hook to use the error context
export const useError = () => {
  return useContext(ErrorContext);
};
